/* =====================================================================
 * queryStoragePersist — shared React Query sessionStorage helpers.
 *
 * The `QueryClient` instance itself stays in the host (`query/client.ts`)
 * because React Query requires a single shared instance per renderer and
 * needs to live where <QueryClientProvider> is mounted.  But the key
 * name used for session persistence (`${tenantPrefix}rq_cache`) and the
 * reset helper are safe to duplicate here so shared components (Sidebar,
 * ProfileTab, ...) can invalidate the persisted snapshot on logout /
 * withdraw without importing host internals.
 *
 * Host + shared must agree on the same tenant-prefixed key; both sides
 * go through `activeTenantKey('rq_cache')` to stay in sync.
 * ===================================================================== */

import { activeTenantKey } from '@auw/tenant';

const RQ_CACHE_STORAGE_SUFFIX = 'rq_cache';

/**
 * Remove the persisted React Query cache from sessionStorage.
 *
 * Safe to call from shared components during logout, account switch,
 * or any other flow that should not leak the previous account's query
 * cache to the next mount.  Failures (quota, private mode, SSR) are
 * swallowed because this is strictly a cleanup hint — the in-memory
 * queryClient is separately invalidated by the caller.
 */
export function clearQuerySessionStorage(): void {
  try {
    sessionStorage.removeItem(activeTenantKey(RQ_CACHE_STORAGE_SUFFIX));
  } catch {
    // storage access errors are non-fatal during auth reset
  }
}
